import React, { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  MoreVertical,
  Shield,
  Crown,
  User,
  Eye,
  Trash2,
  ChevronDown,
  Mail,
  Clock,
  XCircle,
} from 'lucide-react';
import { Spinner } from '../../design-system';
import { useOrganization, OrganizationMember, OrganizationInvitation } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';
import InviteMemberModal from './InviteMemberModal';
import toast from '../../design-system/toast';

interface MembersListProps {
  organizationId: string;
  organizationName: string;
}

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'owner':
      return <Crown className="w-4 h-4 text-amber-500" />;
    case 'admin':
      return <Shield className="w-4 h-4 text-brand-primary" />;
    case 'viewer':
      return <Eye className="w-4 h-4 text-ui-text-tertiary" />;
    default:
      return <User className="w-4 h-4 text-ui-text-tertiary" />;
  }
};

const getRoleBadge = (role: string) => {
  const baseClasses = 'px-2 py-0.5 text-xs font-medium rounded-full capitalize';
  switch (role) {
    case 'owner':
      return `${baseClasses} bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400`;
    case 'admin':
      return `${baseClasses} bg-brand-primary/20 dark:bg-brand-primary/30 text-brand-primaryDark dark:text-brand-primaryDarkMode`;
    case 'viewer':
      return `${baseClasses} bg-ui-background-tertiary text-ui-text-secondary`;
    default:
      return `${baseClasses} bg-ui-background-tertiary text-ui-text-secondary`;
  }
};

const MembersList: React.FC<MembersListProps> = ({ organizationId, organizationName }) => {
  const { user } = useAuth();
  const {
    getMembers,
    getInvitations,
    removeMember,
    updateMemberRole,
    revokeInvitation,
    isAdmin,
    isOwner,
  } = useOrganization();

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [membersData, invitationsData] = await Promise.all([
        getMembers(organizationId),
        isAdmin ? getInvitations(organizationId) : Promise.resolve([]),
      ]);

      setMembers(membersData);
      setInvitations(invitationsData.filter((inv) => inv.status === 'pending'));
    } catch (err: any) {
      console.error('Failed to load organization data:', err);
      setError(err.data?.detail || err.message || 'Failed to load organization data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [organizationId]);

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the organization?`)) {
      return;
    }

    try {
      setProcessingAction(memberId);
      await removeMember(organizationId, memberId);
      toast.success(`${memberName} has been removed`);
      await loadData();
    } catch (err: any) {
      console.error('Failed to remove member:', err);
      toast.error(err.data?.detail || err.message || 'Failed to remove member');
    } finally {
      setProcessingAction(null);
      setActionMenuOpen(null);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: string, memberName: string) => {
    try {
      setProcessingAction(memberId);
      await updateMemberRole(organizationId, memberId, newRole);
      toast.success(`${memberName}'s role updated to ${newRole}`);
      await loadData();
    } catch (err: any) {
      console.error('Failed to update role:', err);
      toast.error(err.data?.detail || err.message || 'Failed to update role');
    } finally {
      setProcessingAction(null);
      setActionMenuOpen(null);
    }
  };

  const handleRevokeInvitation = async (invitationId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to revoke the invitation for ${email}?`)) {
      return;
    }

    try {
      setProcessingAction(invitationId);
      await revokeInvitation(organizationId, invitationId);
      toast.success('Invitation revoked');
      await loadData();
    } catch (err: any) {
      console.error('Failed to revoke invitation:', err);
      toast.error(err.data?.detail || err.message || 'Failed to revoke invitation');
    } finally {
      setProcessingAction(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={loadData}
          className="mt-2 text-sm text-brand-primary dark:text-brand-primaryDarkMode hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-ui-text-tertiary" />
          <h3 className="text-lg font-semibold text-ui-text-primary">
            Members ({members.length})
          </h3>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          disabled={!isAdmin}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-black
                     bg-brand-primary hover:bg-brand-primaryDark rounded-lg transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-primary"
        >
          <UserPlus className="w-4 h-4" />
          Invite
        </button>
      </div>

      {/* Members List */}
      <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary overflow-hidden">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-4 border-b border-ui-border-primary last:border-b-0"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-ui-background-tertiary rounded-full flex items-center justify-center">
                {getRoleIcon(member.role)}
              </div>
              <div>
                <p className="font-medium text-ui-text-primary">
                  {member.user_name || member.user_email || 'Unknown User'}
                  {member.user_id === user?.id && (
                    <span className="ml-2 text-xs text-ui-text-tertiary">(You)</span>
                  )}
                </p>
                {member.user_email && member.user_name && (
                  <p className="text-sm text-ui-text-tertiary">
                    {member.user_email}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className={getRoleBadge(member.role)}>{member.role}</span>

              {/* Action Menu — shown on rows that could have an action for an admin.
                  Rows the viewer can't affect either way (owner / self / admin-modifying-admin)
                  stay hidden. On the remaining rows the kebab is visible but disabled for non-admins. */}
              {member.role !== 'owner' && member.user_id !== user?.id && (isOwner || member.role !== 'admin') && (
                <div className="relative">
                  <button
                    onClick={() => setActionMenuOpen(actionMenuOpen === member.id ? null : member.id)}
                    className="p-1 hover:bg-ui-background-tertiary rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    disabled={!isAdmin || processingAction === member.id}
                  >
                    {processingAction === member.id ? (
                      <Spinner size={16} tone="current" />
                    ) : (
                      <MoreVertical className="w-4 h-4 text-ui-text-tertiary" />
                    )}
                  </button>

                  {actionMenuOpen === member.id && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setActionMenuOpen(null)}
                      />
                      <div className="absolute right-0 mt-1 w-48 bg-ui-background-secondary rounded-lg shadow-lg border border-ui-border-primary py-1 z-50">
                        {/* Role options */}
                        {isAdmin && (
                          <>
                            {member.role !== 'admin' && (
                              <button
                                onClick={() => handleUpdateRole(member.user_id, 'admin', member.user_name || member.user_email || 'User')}
                                className="w-full px-3 py-2 text-sm text-left text-ui-text-secondary hover:bg-ui-background-tertiary"
                              >
                                Make Admin
                              </button>
                            )}
                            {member.role !== 'member' && (
                              <button
                                onClick={() => handleUpdateRole(member.user_id, 'member', member.user_name || member.user_email || 'User')}
                                className="w-full px-3 py-2 text-sm text-left text-ui-text-secondary hover:bg-ui-background-tertiary"
                              >
                                Make Member
                              </button>
                            )}
                            {member.role !== 'viewer' && (
                              <button
                                onClick={() => handleUpdateRole(member.user_id, 'viewer', member.user_name || member.user_email || 'User')}
                                className="w-full px-3 py-2 text-sm text-left text-ui-text-secondary hover:bg-ui-background-tertiary"
                              >
                                Make Viewer (Read-only)
                              </button>
                            )}
                            <div className="border-t border-ui-border-primary my-1" />
                          </>
                        )}
                        <button
                          onClick={() => handleRemoveMember(member.user_id, member.user_name || member.user_email || 'User')}
                          className="w-full px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4 inline mr-2" />
                          Remove Member
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pending Invitations */}
      {isAdmin && invitations.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-ui-text-secondary flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Invitations ({invitations.length})
          </h4>
          <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary overflow-hidden">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between p-4 border-b border-ui-border-primary last:border-b-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                    <Mail className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium text-ui-text-primary">
                      {invitation.email}
                    </p>
                    <p className="text-xs text-ui-text-tertiary">
                      Invited as {invitation.role} · Expires{' '}
                      {new Date(invitation.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleRevokeInvitation(invitation.id, invitation.email)}
                  disabled={processingAction === invitation.id}
                  className="p-2 text-ui-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                  title="Revoke invitation"
                >
                  {processingAction === invitation.id ? (
                    <Spinner size={16} tone="current" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          loadData(); // Refresh to show new invitation
        }}
        organizationId={organizationId}
        organizationName={organizationName}
      />
    </div>
  );
};

export default MembersList;
